// Shared data table: TanStack Table renders; search, sorting and paging run on the
// client (`rows`) or on the server (`server` prop) for large operational lists.
import {
  createColumnHelper,
  flexRender,
  tableFeatures,
  useTable,
  type RowData,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Search,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button, Skeleton } from './kit';

const features = tableFeatures({});

export interface Column<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Enables client-side sorting on this column. */
  sortValue?: (row: T) => string | number | null | undefined;
  align?: 'right';
  width?: number | string;
}

export interface ServerPaging {
  total: number;
  page: number;
  pageSize: number;
  onPage: (page: number) => void;
  search: string;
  onSearch: (q: string) => void;
}

export function DataTable<T extends RowData>({
  columns,
  rows,
  rowKey,
  loading,
  searchPlaceholder,
  searchText,
  toolbar,
  onRowClick,
  empty,
  pageSize = 25,
  initialSort,
  server,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  searchPlaceholder?: string;
  /** Text a row is matched against for client-side search. */
  searchText?: (row: T) => string;
  toolbar?: ReactNode;
  onRowClick?: (row: T) => void;
  empty?: ReactNode;
  pageSize?: number;
  initialSort?: { id: string; desc?: boolean };
  server?: ServerPaging;
}) {
  const [query, setQuery] = useState(server?.search ?? '');
  const [sort, setSort] = useState(initialSort ?? null);
  const [page, setPage] = useState(0);

  // Debounce server-side search.
  useEffect(() => {
    if (!server || query === server.search) return;
    const t = setTimeout(() => server.onSearch(query), 300);
    return () => clearTimeout(t);
  }, [query, server]);

  const visible = useMemo(() => {
    let out = rows;
    if (!server && query.trim() && searchText) {
      const q = query.trim().toLowerCase();
      out = out.filter((r) => searchText(r).toLowerCase().includes(q));
    }
    const col = sort ? columns.find((c) => c.id === sort.id) : undefined;
    if (col?.sortValue) {
      const get = col.sortValue;
      out = [...out].sort((a, b) => {
        const va = get(a) ?? '';
        const vb = get(b) ?? '';
        const cmp =
          typeof va === 'number' && typeof vb === 'number'
            ? va - vb
            : String(va).localeCompare(String(vb), undefined, { numeric: true });
        return sort?.desc ? -cmp : cmp;
      });
    }
    return out;
  }, [rows, query, searchText, sort, columns, server]);

  useEffect(() => setPage(0), [query, sort]);

  const total = server ? server.total : visible.length;
  const size = server ? server.pageSize : pageSize;
  const current = server ? server.page : page;
  const pageRows = server ? visible : visible.slice(current * size, current * size + size);
  const pages = Math.max(1, Math.ceil(total / size));

  const defs = useMemo(() => {
    const helper = createColumnHelper<typeof features, T>();
    return columns.map((c) =>
      helper.display({ id: c.id, header: () => c.header, cell: (ctx) => c.cell(ctx.row.original) }),
    );
  }, [columns]);
  const table = useTable({
    features,
    columns: defs,
    data: pageRows,
    getRowId: (row) => rowKey(row),
  });
  const byId = new Map(columns.map((c) => [c.id, c]));

  const toggleSort = (c: Column<T>) => {
    if (!c.sortValue) return;
    setSort((s) => (s?.id === c.id ? (s.desc ? null : { id: c.id, desc: true }) : { id: c.id }));
  };

  const showToolbar = Boolean(searchText || server || toolbar);
  return (
    <div className="card table-card">
      {showToolbar ? (
        <div className="table-toolbar">
          {searchText || server ? (
            <div className="input-icon">
              <Search aria-hidden />
              <input
                className="input"
                type="search"
                placeholder={searchPlaceholder ?? 'Search'}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={searchPlaceholder ?? 'Search'}
              />
            </div>
          ) : null}
          {toolbar}
          <span className="spacer" />
          <span className="muted small num">
            {loading ? '' : `${total.toLocaleString()} ${total === 1 ? 'result' : 'results'}`}
          </span>
        </div>
      ) : null}
      <div className="table-scroll">
        <table className="table">
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((h) => {
                  const c = byId.get(h.column.id)!;
                  const active = sort?.id === c.id;
                  return (
                    <th
                      key={h.id}
                      className={`${c.sortValue ? 'sortable' : ''} ${c.align === 'right' ? 'right' : ''}`}
                      style={c.width ? { width: c.width } : undefined}
                      onClick={() => toggleSort(c)}
                      aria-sort={active ? (sort?.desc ? 'descending' : 'ascending') : undefined}
                    >
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {c.sortValue ? (
                        <span className="sort">
                          {active ? (
                            sort?.desc ? (
                              <ArrowDown />
                            ) : (
                              <ArrowUp />
                            )
                          ) : (
                            <ChevronsUpDown className="faint" />
                          )}
                        </span>
                      ) : null}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }, (_, i) => (
                <tr key={i}>
                  {columns.map((c) => (
                    <td key={c.id}>
                      <Skeleton width={c.align === 'right' ? 48 : '70%'} />
                    </td>
                  ))}
                </tr>
              ))
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: 0 }}>
                  {query && !server ? (
                    <div className="empty">No matches for “{query}”.</div>
                  ) : (
                    empty
                  )}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={onRowClick ? 'clickable' : ''}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick ? (e) => e.key === 'Enter' && onRowClick(row.original) : undefined
                  }
                >
                  {row.getAllCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={byId.get(cell.column.id)?.align === 'right' ? 'right num' : ''}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {total > size ? (
        <div className="table-footer">
          <span className="num">
            {current * size + 1}–{Math.min((current + 1) * size, total)} of {total.toLocaleString()}
          </span>
          <div className="row">
            <Button
              size="sm"
              icon={ChevronLeft}
              disabled={current === 0}
              onClick={() => (server ? server.onPage(current - 1) : setPage(current - 1))}
            >
              Previous
            </Button>
            <span className="num">
              Page {current + 1} / {pages}
            </span>
            <Button
              size="sm"
              disabled={current + 1 >= pages}
              onClick={() => (server ? server.onPage(current + 1) : setPage(current + 1))}
            >
              Next
              <ChevronRight />
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
